export type EventType = 'start' | 'end';

export interface ScheduleResult {
  [taskId: string]: {
    start: number;
    end: number;
  };
}

export type NodeRelations = Array<
  [string, EventType, string, EventType, number]
>; // string: from task, Event of from task, string: to task, Event of to task, number: lag time

export type IDuration = Record<string, number | null>;

interface IBuildAndComputeSchedule {
  baseDate: number;
  taskIdsList: string[];
  durations: IDuration;
  childrenDict: Record<string, string[]>;
  relations: NodeRelations;
  customNodeOverrideTime: IDuration;
}

type Node = `${string}:${EventType}`; // TaskID:Start/End

interface Edge {
  from: Node;
  to: Node;
  weight: number;
}

export const buildAndComputeSchedule = ({
  baseDate,
  taskIdsList,
  durations,
  childrenDict,
  relations,
  customNodeOverrideTime,
}: IBuildAndComputeSchedule) => {
  const getNode = (task: string, ev: EventType): Node =>
    `${task}:${ev}` as Node;

  const edges: Edge[] = [];
  const nodeSet = new Set<Node>();

  const addEdge = (from: Node, to: Node, weight: number = 0) => {
    edges.push({ from, to, weight });
    nodeSet.add(from);
    nodeSet.add(to);
  };

  for (const task of taskIdsList) {
    const s = getNode(task, 'start');
    const e = getNode(task, 'end');
    const dur = durations?.[task] ?? 0;
    addEdge(s, e, dur);
  }

  for (const [parent, children] of Object.entries(childrenDict)) {
    for (const child of children) {
      if (!taskIdsList.includes(child)) continue;
      const cs = getNode(child, 'start');
      const ce = getNode(child, 'end');
      const ps = getNode(parent, 'start');
      const pe = getNode(parent, 'end');

      addEdge(ps, cs, 0);
      addEdge(ce, pe, 0);
    }
  }

  for (const [fromTask, fromEvent, toTask, toEvent, lag] of relations) {
    const ft = getNode(fromTask, fromEvent);
    const tt = getNode(toTask, toEvent);
    addEdge(tt, ft, lag ?? 0);
  }

  const allNodes = Array.from(nodeSet);

  const successors: Record<Node, Array<{ to: Node; weight: number }>> = {};
  const predecessors: Record<Node, Array<{ from: Node; weight: number }>> = {};

  for (const e of edges) {
    if (!Object.keys(successors).includes(e.from)) successors[e.from] = [];
    if (!Object.keys(predecessors).includes(e.to)) predecessors[e.to] = [];

    successors[e.from].push({ to: e.to, weight: e.weight });
    predecessors[e.to].push({ from: e.from, weight: e.weight });
  }

  const indegree: Record<Node, number> = {};
  for (const n of allNodes) indegree[n] = predecessors[n]?.length ?? 0;

  const queue: Node[] = allNodes.filter((n) => indegree[n] === 0);
  const topoOrder: Node[] = [];
  const indegreeCopy = { ...indegree };

  while (queue.length > 0) {
    const current = queue.shift()!;
    topoOrder.push(current);

    for (const { to } of successors[current] || []) {
      indegreeCopy[to]--;
      if (indegreeCopy[to] === 0) queue.push(to);
    }
  }

  if (topoOrder.length !== allNodes.length) {
    throw new Error('Cycle detected in the schedule graph!');
  }

  const earliestFirstPass: Record<Node, number> = {};
  for (const node of topoOrder) {
    let maxFromPredecessors = baseDate;
    for (const { from, weight } of predecessors[node] || []) {
      const predTime = earliestFirstPass[from];
      if (predTime !== undefined) {
        maxFromPredecessors = Math.max(maxFromPredecessors, predTime + weight);
      }
    }
    earliestFirstPass[node] = maxFromPredecessors;
  }

  const rolledStartMemo: Record<string, number> = {};

  function getRolledStart(taskId: string): number {
    if (rolledStartMemo[taskId] !== undefined) return rolledStartMemo[taskId];

    const directChildren = (childrenDict[taskId] || []).filter((ch) =>
      taskIdsList.includes(ch)
    );

    let value: number;

    if (directChildren.length === 0) {
      value = earliestFirstPass[getNode(taskId, 'start')] ?? baseDate;
    } else {
      const childStarts = directChildren.map((ch) => getRolledStart(ch));
      value = childStarts.length > 0 ? Math.min(...childStarts) : baseDate;
    }

    rolledStartMemo[taskId] = value;
    return value;
  }

  const rolledStarts: Record<string, number> = {};
  for (const task of taskIdsList) {
    rolledStarts[task] = getRolledStart(task);
  }

  const finalEarliest: Record<Node, number> = {};
  for (const node of topoOrder) {
    let maxFromPredecessors = baseDate;

    for (const { from, weight } of predecessors[node] || []) {
      const predTime = finalEarliest[from];
      if (predTime !== undefined) {
        maxFromPredecessors = Math.max(maxFromPredecessors, predTime + weight);
      }
    }

    if (node.endsWith(':start')) {
      const taskId = node.split(':')[0]!;
      const minAllowed = Math.max(
        customNodeOverrideTime?.[taskId] ?? 0,
        rolledStarts?.[taskId] ?? 0,
        baseDate
      );
      finalEarliest[node] = Math.max(maxFromPredecessors, minAllowed);
    } else {
      finalEarliest[node] = maxFromPredecessors;
    }
  }

  const schedule: ScheduleResult = {};
  for (const task of taskIdsList) {
    const sNode = getNode(task, 'start');
    const eNode = getNode(task, 'end');
    const startTime = finalEarliest[sNode] ?? baseDate;
    const endTime = finalEarliest[eNode] ?? startTime + (durations[task] ?? 0);

    schedule[task] = { start: startTime, end: endTime - 1 * 60 * 1000 };
  }

  return schedule;
};
